import React, { useState, useRef, useEffect } from 'react';

interface PDFViewerProps {
  pdfUrl?: string;
  pdfContent?: string;
  pdfFile?: File;
  title?: string;
  className?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = React.memo(({
  pdfUrl,
  pdfContent,
  pdfFile,
  title = 'PDF Document',
  className = '',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Create object URL from File if provided
  useEffect(() => {
    if (pdfFile && pdfFile.type === 'application/pdf') {
      const url = URL.createObjectURL(pdfFile);
      setObjectUrl(url);
      
      // Cleanup function to revoke object URL
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    // Return empty cleanup if no file
    return () => {};
  }, [pdfFile]);
  
  // Initialize loading state based on available content
  useEffect(() => {
    if (!pdfUrl && !pdfContent && !pdfFile && !objectUrl) {
      setError('No PDF content provided');
      setIsLoading(false);
      return;
    }
    
    // Set a reasonable timeout for PDF loading
    const loadingTimeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 3000); // 3 second timeout
    
    return () => clearTimeout(loadingTimeout);
  }, [pdfUrl, pdfContent, pdfFile, objectUrl, isLoading]);

  if (isLoading) {
    return (
      <div className={`flex-1 bg-gray-100 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto mb-2"></div>
          <span className="text-gray-500 text-sm">Loading PDF...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex-1 bg-gray-50 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="text-red-500 text-sm mb-2">⚠️ Error loading PDF</div>
          <span className="text-gray-500 text-xs">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 bg-gray-50 rounded-lg overflow-hidden ${className}`}>
      {objectUrl ? (
        // Prioritize object URL from File
        <iframe
          ref={iframeRef}
          src={`${objectUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
          className="w-full h-full border-none"
          title={title}
          loading="lazy"
          onLoad={() => {
            setIsLoading(false);
            setError(null);
          }}
          onError={() => {
            setError('Failed to load PDF file');
            setIsLoading(false);
          }}
        />
      ) : pdfUrl ? (
        <iframe
          ref={iframeRef}
          src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
          className="w-full h-full border-none"
          title={title}
          loading="lazy"
          onLoad={() => {
            setIsLoading(false);
            setError(null);
          }}
          onError={() => {
            setError('Failed to load PDF');
            setIsLoading(false);
          }}
        />
      ) : pdfContent ? (
        <iframe
          ref={iframeRef}
          src={`data:application/pdf;base64,${pdfContent}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
          className="w-full h-full border-none"
          title={title}
          loading="lazy"
          onLoad={() => {
            setIsLoading(false);
            setError(null);
          }}
          onError={() => {
            setError('Failed to load PDF content');
            setIsLoading(false);
          }}
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-gray-400 mb-2">📄</div>
            <span className="text-gray-500 text-sm">PDF preview unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
});

PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;
